import React from 'react';
import { X, Plus, Check } from '../../design-system/icons';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { titleCase } from './campaignDetailsParts';

interface ChipOption {
  value: string;
  label: string;
}

interface ChipMultiSelectProps {
  label: string;
  /** Selected values (canonical lowercase strings, matches backend). */
  values: string[];
  onChange: (values: string[]) => void;
  /** Fixed option list. Plain strings get title-cased for display. */
  options: (string | ChipOption)[];
  /** Lets the user type a free-form value not in `options` (e.g. categories). */
  allowCustom?: boolean;
  /** Rendered inline next to the label — used for the per-field comment dot. */
  labelExtra?: React.ReactNode;
  /** Replaces the default checkbox list inside the popover — used by the fields
   *  whose Figma menu is a card/tile picker rather than a plain list. `options`
   *  is still read for the chip labels. */
  renderMenu?: () => React.ReactNode;
  /** Sizing for the popover when `renderMenu` supplies its own layout. */
  menuClassName?: string;
  /** For fields where an empty selection means "no filter" (the gender fields):
   *  adds an "All" row at the top of the list that clears the selection, and
   *  shows a standing "All" chip while nothing is selected. Mirrors the
   *  create-campaign wizard's PillGroup, which models All the same way. */
  allOption?: boolean;
}

/**
 * One generic chip multiselect used for every multiselect field in
 * CampaignEditForm (languages/regions/genders/categories/tiers/
 * creator_genders/content-types/add-ons) — a DRY simplification over the
 * wizard's 8 bespoke per-field implementations (PillGroup/MultiSelectCombobox/
 * category tiles/tier cards in apps/brand/.../NewCampaign/StepTargeting.tsx).
 *
 * Selected values render as removable pills; the popover lists every option as
 * a checkbox row. Deliberately simple (no search/combobox) for v1.
 */
export function ChipMultiSelect({
  label,
  values,
  onChange,
  options,
  allowCustom,
  labelExtra,
  allOption,
  renderMenu,
  menuClassName,
}: ChipMultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [customInput, setCustomInput] = React.useState('');
  const normalized: ChipOption[] = options.map((o) => (typeof o === 'string' ? { value: o, label: titleCase(o) } : o));

  // Toggle keeps the dropdown open so several options can be (un)checked in one
  // pass — matches the Figma checkbox-list spec (options stay listed, selected
  // ones show a filled brand checkbox rather than disappearing into pills).
  const toggle = (value: string) =>
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  const add = (value: string) => {
    if (!value || values.includes(value)) return;
    onChange([...values, value]);
    setCustomInput('');
  };
  const remove = (value: string) => onChange(values.filter((v) => v !== value));

  const optionRow = (key: string, checked: boolean, onClick: () => void, text: string) => (
    <button
      key={key}
      type="button"
      role="checkbox"
      aria-checked={checked}
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-body-sm-regular text-foreground hover:bg-muted"
    >
      <span
        className={`grid size-[18px] shrink-0 place-content-center rounded-[5px] border transition-colors ${
          checked ? 'border-(--brand-500) bg-(--brand-500) text-white' : 'border-input'
        }`}
      >
        {checked && <Check size={12} weight="bold" />}
      </span>
      <span className="truncate">{text}</span>
    </button>
  );

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex min-h-6 items-center gap-1.5 text-body-sm-regular text-muted-foreground">
        <span>{label}</span>
        {labelExtra}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Nothing selected + allOption = the field is unfiltered; say so with a
            standing chip rather than leaving the row visually empty. It has no
            remove affordance — "All" is the absence of a selection. */}
        {allOption && values.length === 0 && (
          <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-body-sm-medium text-foreground">
            All
          </span>
        )}
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-body-sm-medium text-foreground"
          >
            {normalized.find((o) => o.value === v)?.label ?? titleCase(v)}
            <button type="button" onClick={() => remove(v)} className="text-muted-foreground hover:text-foreground">
              <X size={12} weight="bold" />
            </button>
          </span>
        ))}
        {/* Radix Popover (not an absolutely-positioned div): its content is
            portalled to the body, so the list escapes CardShell's
            `overflow-hidden` instead of being clipped by the card, and Radix
            flips it above the trigger when there's no room below. */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 text-body-sm-medium text-muted-foreground hover:border-foreground hover:text-foreground"
            >
              <Plus size={12} weight="bold" />
              Add
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            sideOffset={6}
            className={
              menuClassName
                ? `overflow-auto rounded-xl border-border bg-card shadow-md ${menuClassName}`
                : 'max-h-56 w-52 overflow-auto rounded-lg border-border bg-card p-1 shadow-md'
            }
          >
            {renderMenu?.()}
            {!renderMenu && allOption && optionRow('__all__', values.length === 0, () => onChange([]), 'All')}
            {!renderMenu &&
              normalized.map((o) => optionRow(o.value, values.includes(o.value), () => toggle(o.value), o.label))}
            {!renderMenu && allowCustom && (
              <div className="flex gap-1 border-t border-border p-1 pt-2">
                <input
                  value={customInput}
                  onChange={(e) => setCustomInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') add(customInput.trim().toLowerCase());
                  }}
                  placeholder="Custom…"
                  className="min-w-0 flex-1 rounded-md border border-border px-2 py-1 text-body-sm-regular"
                />
                <button
                  type="button"
                  onClick={() => add(customInput.trim().toLowerCase())}
                  className="rounded-md bg-foreground px-2 py-1 text-body-sm-medium text-background"
                >
                  Add
                </button>
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
